import { BaseSocketService, SocketServiceConfig } from 'app/socketService';
import { MiaChatEvent, MiaChatSubscription, SubscribeConversationRequest, UnsubscribeConversationRequest } from '../types';

// ----------- MiaChatV2 Socket Service -----------
class MiaChatV2SocketService extends BaseSocketService {
    private static instance: MiaChatV2SocketService;
    private subscriptions: Map<string, MiaChatSubscription> = new Map();
    private eventCache: Map<string, MiaChatEvent> = new Map();
    private readonly STORAGE_KEY = 'miaChatV2_activeConversationId'; // LocalStorage key
    private connectListenerSetup: boolean = false; // Guard to prevent duplicate listeners

    constructor(config: SocketServiceConfig) {
        super(config);
    }

    // Singleton pattern
    static getInstance(config?: SocketServiceConfig): MiaChatV2SocketService {
        if (!MiaChatV2SocketService.instance) {
            if (!config) {
                throw new Error('MiaChatV2SocketService requires config for first initialization');
            }
            MiaChatV2SocketService.instance = new MiaChatV2SocketService(config);
        }
        return MiaChatV2SocketService.instance;
    }

    // Service name
    getServiceName(): string {
        return 'MiaChatV2SocketService';
    }

    // Events this service handles (matching MCPContext events)
    getEvents(): string[] {
        return [
            'token_usage',           // Token usage updates
            'agent_message',         // Agent message updates
            'processing_status',     // Processing status updates
            'streaming_section_response',    // Streaming response updates
            'conversation_update',   // General conversation updates
            'email_generation',      // Email generation events
            'suggestion_update',     // Suggestion updates
            'executing_tool_call',   // Tool call execution started
            'tool_call_completed',   // Tool call execution completed
            'subscribe_success',     // Subscription confirmation
            'subscribe_error',       // Subscription error
            'unsubscribe_success',   // Unsubscription confirmation
            'unsubscribe_error',     // Unsubscription error
        ];
    }

    // Subscribe to conversation events
    subscribeToConversation(
        conversationId: string, 
        callback: (data: MiaChatEvent) => void
    ): boolean {
        if (!conversationId || typeof callback !== 'function') {
            console.error('❌ Invalid conversation ID or callback for subscription');
            return false;
        }

        // Check if already subscribed
        if (this.subscriptions.has(conversationId)) {
            console.log(`📡 Already subscribed to conversation: ${conversationId}`);
            // Update callback in case it changed
            const existingSub = this.subscriptions.get(conversationId);
            if (existingSub) {
                existingSub.callback = callback;
            }
            return true;
        }

        // Add to subscriptions map
        this.subscriptions.set(conversationId, {
            conversation_id: conversationId,
            callback,
            active: true
        });

        // CRITICAL: Store conversation_id in localStorage for reconnect restore
        try {
            localStorage.setItem(this.STORAGE_KEY, conversationId);
        } catch (error) {
            // Silently fail - localStorage might be disabled
        }

        // Emit subscribe event to server - just conversation ID as string
        this.emit('subscribe', conversationId);

        console.log(`📡 Subscribed to conversation: ${conversationId}`);
        return true;
    }

    // Unsubscribe from conversation events
    unsubscribeFromConversation(conversationId: string): boolean {
        if (!conversationId) {
            console.error('❌ Invalid conversation ID for unsubscription');
            return false;
        }

        const subscription = this.subscriptions.get(conversationId);
        if (!subscription) {
            console.log(`📡 No active subscription found for conversation: ${conversationId}`);
            return false;
        }

        // Mark as inactive
        subscription.active = false;

        // Emit unsubscribe event to server - just conversation ID as string
        this.emit('unsubscribe', conversationId);

        // Remove from subscriptions map
        this.subscriptions.delete(conversationId);

        // CRITICAL: Remove conversation_id from localStorage when unsubscribing
        try {
            const storedId = localStorage.getItem(this.STORAGE_KEY);
            if (storedId === conversationId) {
                localStorage.removeItem(this.STORAGE_KEY);
            }
        } catch (error) {
            // Silently fail - localStorage might be disabled
        }

        console.log(`📡 Unsubscribed from conversation: ${conversationId}`);
        return true;
    }

    // Get active subscriptions
    getActiveSubscriptions(): string[] {
        return Array.from(this.subscriptions.keys()).filter(id => {
            const sub = this.subscriptions.get(id);
            return sub?.active === true;
        });
    }

    // Check if subscribed to conversation
    isSubscribedToConversation(conversationId: string): boolean {
        const subscription = this.subscriptions.get(conversationId);
        return subscription?.active === true;
    }

    // Manually remove subscription from local map (for syncing with server state)
    // This is useful when server unsubscribes us externally (e.g., via debug panel)
    removeSubscriptionLocally(conversationId: string): boolean {
        if (!conversationId) {
            return false;
        }
        
        const subscription = this.subscriptions.get(conversationId);
        if (subscription) {
            this.subscriptions.delete(conversationId);
            console.log(`🔧 Removed subscription from local map for conversation: ${conversationId}`);
            return true;
        }
        return false;
    }

    // Get subscription count
    getSubscriptionCount(): number {
        return this.subscriptions.size;
    }

    // Clear all subscriptions
    clearAllSubscriptions(): void {
        this.subscriptions.forEach((subscription, conversationId) => {
            // Only unsubscribe if socket is connected
            if (this.isConnected()) {
                this.emit('unsubscribe', conversationId);
            }
            // Mark as inactive but keep in map for potential restoration
            subscription.active = false;
        });
        // Don't clear subscriptions map - keep them for restoration on reconnect
        console.log('📡 Marked all conversation subscriptions as inactive');
    }

    // Handle incoming events
    private handleIncomingEvent(eventName: string, data: any): void {
        try {
            // Add to event history
            this.addEventToHistory(eventName, data);

            // Process based on event type
            switch (eventName) {
                case 'token_usage':
                    this.handleTokenUsage(data);
                    break;
                case 'agent_message':
                    this.handleAgentMessage(data);
                    break;
                case 'processing_status':
                    this.handleProcessingStatus(data);
                    break;
                case 'streaming_section_response':
                    this.handleStreamingResponse(data);
                    break;
                case 'conversation_update':
                    this.handleConversationUpdate(data);
                    break;
                case 'email_generation':
                    this.handleEmailGeneration(data);
                    break;
                case 'suggestion_update':
                    this.handleSuggestionUpdate(data);
                    break;
                case 'executing_tool_call':
                    this.handleExecutingToolCall(data);
                    break;
                case 'tool_call_completed':
                    this.handleToolCallCompleted(data);
                    break;
                case 'subscribe_success':
                    this.handleSubscribeSuccess(data);
                    break;
                case 'subscribe_error':
                    this.handleSubscribeError(data);
                    break;
                case 'unsubscribe_success':
                    this.handleUnsubscribeSuccess(data);
                    break;
                case 'unsubscribe_error':
                    this.handleUnsubscribeError(data);
                    break;
                default:
                    console.log(`📡 Unhandled event: ${eventName}`, data);
            }
        } catch (error) {
            console.error(`❌ Error handling event '${eventName}':`, error);
        }
    }

    // Event handlers
    private handleTokenUsage(data: any): void {
        console.log('📊 Token usage update:', data);
        // Broadcast to all active subscriptions
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleAgentMessage(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleProcessingStatus(data: any): void {
        console.log('📊 Processing status update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message || {},
            processing_type: data.processing_type || 'processing',
            status: data.status || 'processing',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleStreamingResponse(data: any): void {
        console.log('📡 Streaming section response:', data);
        this.broadcastToSubscriptions(data);
    }

    private handleConversationUpdate(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            suggestions: data.suggestions,
            next_action: data.next_action,
            is_input_restricted_to_suggestions: data.is_input_restricted_to_suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleEmailGeneration(data: any): void {
        console.log('📧 Email generation:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            email_content: data.email_content,
            status: data.status || 'success',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSuggestionUpdate(data: any): void {
        console.log('💡 Suggestion update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            suggestions: data.suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleExecutingToolCall(data: any): void {
        console.log('🔧 Executing tool call:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Executing tool: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'processing',
            status: 'processing',
            tool_name: data.tool_name,
            tool_parameters: data.tool_parameters,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleToolCallCompleted(data: any): void {
        console.log('✅ Tool call completed:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Tool completed: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'completed',
            status: 'completed',
            tool_name: data.tool_name,
            tool_result: data.tool_result,
            execution_time: data.execution_time,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSubscribeSuccess(data: any): void {
        console.log('✅ Subscribe success:', data);
        const conversationId = data.conversation_id;
        if (conversationId && this.subscriptions.has(conversationId)) {
            const subscription = this.subscriptions.get(conversationId);
            if (subscription) {
                subscription.active = true;
                console.log(`📡 Successfully subscribed to conversation: ${conversationId}`);
            }
        }
    }

    private handleSubscribeError(data: any): void {
        console.error('❌ Subscribe error:', data);
        const conversationId = data.conversation_id;
        if (conversationId && this.subscriptions.has(conversationId)) {
            this.subscriptions.delete(conversationId);
            console.log(`📡 Removed failed subscription for conversation: ${conversationId}`);
        }
    }

    private handleUnsubscribeSuccess(data: any): void {
        console.log('✅ Unsubscribe success received:', data);
        // Note: Local subscription removal is handled in connect() method
        // to ensure it happens even for external unsubscribes
    }

    private handleUnsubscribeError(data: any): void {
        console.error('❌ Unsubscribe error:', data);
    }

    // Broadcast event to all active subscriptions
    private broadcastToSubscriptions(eventData: MiaChatEvent): void {
        const conversationId = eventData.conversation_id;
        
        if (conversationId && conversationId !== 'unknown') {
            // Send to specific conversation subscription
            const subscription = this.subscriptions.get(conversationId);
            if (subscription && subscription.active) {
                try {
                    subscription.callback(eventData);
                } catch (error) {
                    console.error(`❌ Error in subscription callback for ${conversationId}:`, error);
                }
            }
        } else {
            // Broadcast to all active subscriptions if no specific conversation ID
            this.subscriptions.forEach((subscription, id) => {
                if (subscription.active) {
                    try {
                        subscription.callback(eventData);
                    } catch (error) {
                        console.error(`❌ Error in subscription callback for ${id}:`, error);
                    }
                }
            });
        }
    }

    // Override connect to set up event listeners
    connect(): void {
        super.connect();
        
        // Set up event listeners for all MiaChatV2 events
        this.getEvents().forEach(eventName => {
            this.on(eventName, (data) => {
                this.handleIncomingEvent(eventName, data);
            });
        });
        
        // Listen for unsubscribe_success events to sync local state
        // This handles cases where unsubscribe happens externally (e.g., debug panel)
        if (this.socket) {
            this.socket.on('unsubscribe_success', (data: any) => {
                this.handleUnsubscribeSuccess(data);
                
                // If unsubscribe_success is received, remove from local map if it exists
                // This handles external unsubscribes (e.g., from debug panel)
                const conversationId = data?.conversation_id || data;
                if (conversationId && this.subscriptions.has(conversationId)) {
                    console.log(`🔧 Syncing local subscription map - removing ${conversationId} after unsubscribe_success`);
                    this.subscriptions.delete(conversationId);
                }
            });
            
            // CRITICAL: Listen for connect event to restore subscription from localStorage
            // This ensures subscription is restored on both initial connect and reconnect
            // Use guard to prevent duplicate listeners
            if (!this.connectListenerSetup) {
                this.socket.on('connect', () => {
                    // Delay to ensure socket is fully ready
                    setTimeout(() => {
                        this.restoreSubscriptionFromLocalStorage();
                    }, 500);
                });
                this.connectListenerSetup = true;
            }
        }
    }
    
    // Helper method to restore subscription from localStorage
    private restoreSubscriptionFromLocalStorage(): void {
        // Double check socket is ready
        if (!this.socket) {
            // Retry after a short delay if socket is not available yet
            setTimeout(() => this.restoreSubscriptionFromLocalStorage(), 200);
            return;
        }
        
        if (!this.isConnected()) {
            // Retry after a short delay if not connected yet
            setTimeout(() => this.restoreSubscriptionFromLocalStorage(), 200);
            return;
        }
        
        try {
            const storedConversationId = localStorage.getItem(this.STORAGE_KEY);
            
            if (!storedConversationId) {
                return;
            }
            
            // Check if subscription exists in map
            const existingSubscription = this.subscriptions.get(storedConversationId);
            
            if (existingSubscription) {
                // Subscription exists in map, restore it
                this.emit('subscribe', storedConversationId);
                existingSubscription.active = true;
            } else {
                // Subscription not in map - callback was lost
                // Still emit subscribe to backend so backend knows about subscription
                this.emit('subscribe', storedConversationId);
            }
        } catch (error) {
            // Silently fail - localStorage might be disabled
        }
    }

    // Override disconnect to mark subscriptions as inactive (but keep them for restoration)
    disconnect(): void {
        // Mark subscriptions as inactive but don't clear them
        // This allows them to be restored on reconnect
        this.subscriptions.forEach((subscription) => {
            subscription.active = false;
        });
        super.disconnect();
    }
    
    // Override onReconnect to restore active subscriptions
    protected onReconnect(): void {
        super.onReconnect(); // Call base implementation (emits queued events)
        
        // CRITICAL: Restore subscription from localStorage on reconnect
        // This is called by the manager when reconnect event fires
        // Use the same helper method as connect event
        // Increased delay to ensure socket is fully ready after reconnect
        setTimeout(() => {
            this.restoreSubscriptionFromLocalStorage();
        }, 500); // Increased delay to ensure socket is fully ready
    }

    // Get cached events for a conversation
    getCachedEvents(conversationId: string): MiaChatEvent[] {
        return Array.from(this.eventCache.values())
            .filter(event => event.conversation_id === conversationId)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }

    // Clear cached events for a conversation
    clearCachedEvents(conversationId: string): void {
        const eventsToDelete: string[] = [];
        this.eventCache.forEach((event, key) => {
            if (event.conversation_id === conversationId) {
                eventsToDelete.push(key);
            }
        });
        eventsToDelete.forEach(key => this.eventCache.delete(key));
    }

    // Get service status
    getServiceStatus(): {
        connected: boolean;
        subscriptionCount: number;
        activeSubscriptions: string[];
        eventHistoryCount: number;
    } {
        return {
            connected: this.isConnected(),
            subscriptionCount: this.getSubscriptionCount(),
            activeSubscriptions: this.getActiveSubscriptions(),
            eventHistoryCount: this.getEventHistory().length
        };
    }
}

// Export singleton instance
export const miaChatV2SocketService = MiaChatV2SocketService.getInstance;

// Export the class for testing
export { MiaChatV2SocketService };

// Types are already exported above as interfaces
